import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import GuideButton from '../../common/guide/GuideButton';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface Transaction {
  id: string;
  accountId: string;
  accountName?: string;
  type: string;
  transactionType?: string;
  operationType?: string;
  amount: number;
  debitAmount?: number;
  creditAmount?: number;
  debitBalance?: number;
  description: string;
  documentNo?: string;
  referenceId?: string;
  referenceType?: string;
  date?: string;
  createdAt: string;
  createdBy?: string;
  isIncome: boolean;
  currency?: string;
  exchangeRate?: number;
  paymentMethod?: string;
  previousBalance?: number;
  nextBalance?: number;
  cashboxId?: string;
  cashboxName?: string;
  bankAccountId?: string;
  bankAccountName?: string;
}

interface PaymentReceiptModalProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  companyId: string | null;
}

const PaymentReceiptModal: React.FC<PaymentReceiptModalProps> = ({
  open,
  onClose,
  transaction,
  companyId
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const dateLocale = isTr ? tr : enUS;
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    if (!/[ÃÂÅÄ]/.test(value)) return value;
    try {
      return decodeURIComponent(escape(value));
    } catch {
      return value;
    }
  };
  const [loading, setLoading] = useState(false);

  if (!transaction) return null;

  // İşlem türünü belirle (TEDİYE mi TAHSİLAT mı)
  const isPayment = transaction.transactionType === 'PAYMENT' || (transaction.debitAmount || 0) > 0;
  const isCollection = transaction.transactionType === 'COLLECTION' || (transaction.creditAmount || 0) > 0;
  
  const receiptTitle = isPayment ? tt('TEDİYE FİŞİ', 'PAYMENT RECEIPT') : tt('TAHSİLAT MAKBUZU', 'COLLECTION RECEIPT');
  const amountLabel = isPayment ? tt('Ödenen Tutar', 'Paid Amount') : tt('Tahsil Edilen Tutar', 'Collected Amount');
  const amount = isPayment ? (transaction.debitAmount || transaction.amount) : (transaction.creditAmount || transaction.amount);

  // Para birimi formatla
  const formatCurrency = (value: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Tarih formatla
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return format(date, 'dd.MM.yyyy HH:mm', { locale: dateLocale });
    } catch {
      return dateString;
    }
  };

  // Ödeme yöntemi isimleri
  const getPaymentMethodName = (method?: string) => {
    const methods: Record<string, string> = {
      'cash': tt('Nakit', 'Cash'),
      'credit_card': tt('Kredi Kartı', 'Credit Card'),
      'card_payment': 'POS',
      'bank_transfer': tt('Havale/EFT', 'Bank Transfer/EFT'),
      'check': tt('Çek', 'Check'),
      'pos': 'POS',
      'accrual': tt('Tahakkuk', 'Accrual')
    };
    return method ? methods[method] || method : '-';
  };

  // Yazdırma fonksiyonu
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert(tt('Popup engelleyici nedeniyle yazdırma penceresi açılamadı. Lütfen popup engelleyiciyi devre dışı bırakın.', 'Print window could not be opened because of popup blocker. Please disable the popup blocker.'));
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${receiptTitle}</title>
        <style>
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @page {
              margin-top: 0;
              margin-bottom: 0;
            }
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            background: #f5f5f5;
            font-size: 10pt;
            color: #333;
          }

          .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }

          .header {
            text-align: center;
            border-bottom: 3px solid ${isPayment ? '#f44336' : '#4caf50'};
            padding-bottom: 20px;
            margin-bottom: 30px;
          }

          .header h1 {
            color: ${isPayment ? '#f44336' : '#4caf50'};
            font-size: 28px;
            margin-bottom: 5px;
            font-weight: bold;
          }

          .header .doc-no {
            color: #666;
            font-size: 12px;
            margin-top: 5px;
          }

          .section {
            margin-bottom: 25px;
          }

          .section-title {
            color: #1976d2;
            font-size: 14px;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 8px;
            margin-bottom: 12px;
            font-weight: bold;
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 15px;
          }

          .info-item {
            padding: 10px;
            background: #f9f9f9;
            border-radius: 4px;
            border-left: 3px solid #1976d2;
          }

          .info-item label {
            display: block;
            color: #666;
            font-size: 9pt;
            margin-bottom: 4px;
            font-weight: 600;
          }

          .info-item value {
            display: block;
            color: #333;
            font-size: 10pt;
            font-weight: 500;
          }

          .amount-box {
            background: linear-gradient(135deg, ${isPayment ? '#ffebee' : '#e8f5e9'} 0%, ${isPayment ? '#ffcdd2' : '#c8e6c9'} 100%);
            border: 2px solid ${isPayment ? '#f44336' : '#4caf50'};
            border-radius: 8px;
            padding: 20px;
            text-align: center;
            margin: 30px 0;
          }

          .amount-box label {
            display: block;
            color: #666;
            font-size: 12pt;
            margin-bottom: 10px;
            font-weight: 600;
          }

          .amount-box value {
            display: block;
            color: ${isPayment ? '#d32f2f' : '#388e3c'};
            font-size: 24pt;
            font-weight: bold;
          }

          .description-box {
            background: #f5f5f5;
            border-left: 4px solid #1976d2;
            padding: 15px;
            border-radius: 4px;
            margin: 20px 0;
          }

          .description-box label {
            display: block;
            color: #666;
            font-size: 9pt;
            margin-bottom: 8px;
            font-weight: 600;
          }

          .description-box value {
            display: block;
            color: #333;
            font-size: 10pt;
            line-height: 1.6;
          }

          .footer {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 10px;
          }

          .footer strong {
            color: #1976d2;
            font-size: 12px;
          }

          .print-date {
            color: #999;
            font-size: 9px;
            margin-top: 8px;
          }

          .signature-section {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
            margin-top: 60px;
          }

          .signature-box {
            text-align: center;
            padding-top: 40px;
            border-top: 1px solid #333;
          }

          .signature-box label {
            color: #666;
            font-size: 10pt;
            font-weight: 600;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <h1>${receiptTitle}</h1>
            ${transaction.documentNo ? `<p class="doc-no">${tt('Belge No', 'Document No')}: ${transaction.documentNo}</p>` : ''}
          </div>

          <!-- Account Information -->
          <div class="section">
            <h2 class="section-title">📋 ${tt('Cari Bilgileri', 'Account Information')}</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>${tt('Cari Adı', 'Account Name')}</label>
                <value>${decodeMojibake(transaction.accountName) || tt('Belirtilmemiş', 'Not specified')}</value>
              </div>
              <div class="info-item">
                <label>${tt('İşlem Tarihi', 'Transaction Date')}</label>
                <value>${formatDate(transaction.date || transaction.createdAt)}</value>
              </div>
            </div>
          </div>

          <!-- Payment Information -->
          <div class="section">
            <h2 class="section-title">💳 ${tt('Ödeme Bilgileri', 'Payment Information')}</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>${tt('Ödeme Yöntemi', 'Payment Method')}</label>
                <value>${getPaymentMethodName(transaction.paymentMethod)}</value>
              </div>
              ${transaction.cashboxName ? `
              <div class="info-item">
                <label>${tt('Kasa', 'Cashbox')}</label>
                <value>${decodeMojibake(transaction.cashboxName)}</value>
              </div>
              ` : ''}
              ${transaction.bankAccountName ? `
              <div class="info-item">
                <label>${tt('Banka Hesabı', 'Bank Account')}</label>
                <value>${decodeMojibake(transaction.bankAccountName)}</value>
              </div>
              ` : ''}
              <div class="info-item">
                <label>${tt('Para Birimi', 'Currency')}</label>
                <value>${transaction.currency || 'TRY'}</value>
              </div>
            </div>
          </div>

          <!-- Tutar -->
          <div class="amount-box">
            <label>${amountLabel}</label>
            <value>${formatCurrency(amount, transaction.currency || 'TRY')}</value>
          </div>

          <!-- Description -->
          ${transaction.description ? `
          <div class="description-box">
            <label>${tt('Açıklama', 'Description')}</label>
            <value>${decodeMojibake(transaction.description)}</value>
          </div>
          ` : ''}

          <!-- İmza Alanı -->
          <div class="signature-section">
            <div class="signature-box">
              <label>${isPayment ? tt('Ödeyen', 'Payer') : tt('Tahsil Eden', 'Collector')}</label>
            </div>
            <div class="signature-box">
              <label>${isPayment ? tt('Teslim Alan', 'Receiver') : tt('Teslim Eden', 'Deliverer')}</label>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p><strong>AQUASOFT</strong></p>
            <p class="print-date">${tt('Yazdırma Tarihi', 'Print Date')}: ${new Date().toLocaleString(localeCode)}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: isPayment ? 'error.main' : 'success.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2
          }}
        >
          <Typography variant="h6" component="div" fontWeight="bold">
            {receiptTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton
              guideKey="accounts.paymentReceipt"
              color="inherit"
              sx={{ '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
            />
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          ) : (
            <Box>

            {/* Belge Numarası */}
            {transaction.documentNo && (
              <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, mb: 3, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {tt('Belge Numarası', 'Document Number')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {transaction.documentNo}
                </Typography>
              </Paper>
            )}

            {/* Cari Bilgileri */}
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" mb={2}>
                📋 {tt('Cari Bilgileri', 'Account Information')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">{tt('Cari Adı', 'Account Name')}</Typography>
                    <Typography variant="body1" fontWeight="medium">{decodeMojibake(transaction.accountName) || tt('Belirtilmemiş', 'Not specified')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">{tt('İşlem Tarihi', 'Transaction Date')}</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(transaction.date || transaction.createdAt)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Ödeme Bilgileri */}
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" mb={2}>
                💳 {tt('Ödeme Bilgileri', 'Payment Information')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">{tt('Ödeme Yöntemi', 'Payment Method')}</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {getPaymentMethodName(transaction.paymentMethod)}
                    </Typography>
                  </Box>
                </Grid>
                {transaction.cashboxName && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                      <Typography variant="caption" color="text.secondary">{tt('Kasa', 'Cashbox')}</Typography>
                      <Typography variant="body1" fontWeight="medium">{decodeMojibake(transaction.cashboxName)}</Typography>
                    </Box>
                  </Grid>
                )}
                {transaction.bankAccountName && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                      <Typography variant="caption" color="text.secondary">{tt('Banka Hesabı', 'Bank Account')}</Typography>
                      <Typography variant="body1" fontWeight="medium">{decodeMojibake(transaction.bankAccountName)}</Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">{tt('Para Birimi', 'Currency')}</Typography>
                    <Typography variant="body1" fontWeight="medium">{transaction.currency || 'TRY'}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Tutar */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: isPayment ? 'error.50' : 'success.50',
                border: '2px solid',
                borderColor: isPayment ? 'error.main' : 'success.main',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                my: 3
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={1}>
                {amountLabel}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color={isPayment ? 'error.main' : 'success.main'}>
                {formatCurrency(amount, transaction.currency || 'TRY')}
              </Typography>
            </Paper>

            {/* Açıklama */}
            {transaction.description && (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {tt('Açıklama', 'Description')}
                </Typography>
                <Typography variant="body2">
                  {decodeMojibake(transaction.description)}
                </Typography>
              </Box>
            )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button onClick={handleCloseModal} variant="outlined">
            {tt('Kapat', 'Close')}
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            color={isPayment ? 'error' : 'success'}
          >
            {tt('Yazdır', 'Print')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default PaymentReceiptModal;

